import React, { useEffect, useRef, useState, useCallback } from 'react';
import { TooltipWrapper, StyledTooltip } from './styles';
import { TooltipPlacement, TooltipAlign } from './types';

export const Tooltip: React.FC<
  React.PropsWithChildren<React.PropsWithChildren<ITooltipProps>>
> = props => {
  const {
    children,
    className,
    title,
    placement = TooltipPlacement.BOTTOM,
    width,
    align = TooltipAlign.CENTER,
    wrapperWidth,
    renderOnMouseOver = false,
    forcedVisible = false,
  } = props;

  const [parentWidth, setParentWidth] = useState<number | null>(null);
  const [isHovering, setHoverState] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!window || !containerRef.current) {
      return;
    }

    setParentWidth(containerRef.current.getBoundingClientRect().width);
  }, []);

  const handleMouseHover = useCallback(() => {
    if (!renderOnMouseOver) return null;
    return setHoverState(!isHovering);
  }, [isHovering, renderOnMouseOver]);

  // Fix for invalid hover state due to missed onMouseLeave event when rendering
  if (isHovering && containerRef.current?.matches(':hover') === false) {
    setHoverState(false);
  }

  return (
    <TooltipWrapper
      ref={containerRef}
      className={className}
      style={{ width: wrapperWidth }}
      onMouseEnter={handleMouseHover}
      onMouseLeave={handleMouseHover}
      renderOnMouseOver={renderOnMouseOver}
      isHovering={isHovering || forcedVisible}
    >
      {(forcedVisible || !renderOnMouseOver || isHovering) && title && (
        <StyledTooltip
          placement={placement}
          width={width}
          align={align}
          parentWidth={parentWidth}
          data-test="Tooltip"
          role="tooltip"
        >
          {title}
        </StyledTooltip>
      )}
      {children}
    </TooltipWrapper>
  );
};

interface ITooltipProps {
  className?: string;
  title?: string;
  placement?: TooltipPlacement;
  align?: TooltipAlign;
  width?: number;
  wrapperWidth?: number;
  renderOnMouseOver?: boolean;
  forcedVisible?: boolean;
}
